import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";
import axios from "axios";

import { API, resolveImg } from "@/config";

export default function GalleryPage() {
  const [gallery, setGallery] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const r = await axios.get(`${API}/gallery`);
        setGallery(r.data);
      } catch (err) {
        console.error("Failed to load gallery:", err.message);
      }
    };
    fetchGallery();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch on mount; API is a module constant

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section className="bg-[#1A1A1A] py-20 md:py-28">
        <div className="section-container text-center">
          <p className="text-[#E8922F] text-sm uppercase tracking-widest font-semibold mb-3">Our Moments</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Gallery
          </h1>
          <p className="text-gray-400 text-base md:text-lg mt-4 max-w-xl mx-auto">
            Capturing the beautiful moments of worship, fellowship, and community.
          </p>
        </div>
      </section>

      {/* Bento Grid Gallery */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="section-container">
          {gallery.length === 0 ? (
            <p className="text-center text-[#4A4A4A] text-lg">No gallery images yet. Check back soon!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {gallery.map((item, i) => {
                const spans = [
                  "md:col-span-7", "md:col-span-5",
                  "md:col-span-5", "md:col-span-7",
                  "md:col-span-6", "md:col-span-6",
                  "md:col-span-8", "md:col-span-4",
                ];
                const span = spans[i % spans.length];
                const height = i % 3 === 0 ? "h-[300px] md:h-[400px]" : "h-[250px] md:h-[320px]";
                return (
                  <div
                    key={item.id}
                    className={`${span} rounded-2xl overflow-hidden cursor-pointer group relative`}
                    onClick={() => setSelectedImage(item)}
                    data-testid={`gallery-item-${item.id}`}
                  >
                    <img
                      src={resolveImg(item.image_url)}
                      alt={item.title}
                      className={`w-full ${height} object-cover group-hover:scale-105 transition-transform duration-500`}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-6">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item.title}</h3>
                        {item.description && <p className="text-white/80 text-sm">{item.description}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none">
          {selectedImage && (
            <div className="relative">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                data-testid="lightbox-close"
              >
                <X size={20} />
              </button>
              <img
                src={resolveImg(selectedImage.image_url)}
                alt={selectedImage.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="p-6 bg-black/80">
                <h3 className="text-white font-bold text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{selectedImage.title}</h3>
                {selectedImage.description && <p className="text-white/70 text-sm mt-1">{selectedImage.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
